import React, { useState, useEffect } from "react";
import { useLegalEntities } from "../hooks/useLegalEntities";
import CardLoading from "./CardLoading";
import LegalEntityForm from "./LegalEntityForm";
import { useCanWrite, AGENT_READ_ONLY_MESSAGE } from "../hooks/useAccountConfig";
import AgentReadOnlyBanner from "./AgentReadOnlyBanner";
import type { LegalEntity } from "../types";

const LegalEntityList: React.FC = () => {
  const { legalEntities, isLoading, getByName, upsert, rename } = useLegalEntities();
  // DNC-390: Agents may read legal entities but not add, rename, or edit one. This
  // page is also sysadmin-gated in App.tsx, so it only matters for a user who is
  // both — which is how it is normally reached in a dev environment.
  const canWrite = useCanWrite();
  const [editEntity, setEditEntity] = useState<LegalEntity | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [renameState, setRenameState] = useState<{ origName: string; newName: string } | null>(null);

  // Auto-load the first (or default) legal entity when list loads
  useEffect(() => {
    if (legalEntities && legalEntities.length > 0 && !editEntity && !isAdding) {
      const defaultEntity = legalEntities.find((le) => le.isDefault) ?? legalEntities[0];
      handleSelectEntity(defaultEntity.legalName);
    }
  }, [legalEntities]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectEntity = async (legalName: string) => {
    try {
      const entity = await getByName(legalName);
      setEditEntity(entity);
      setIsAdding(false);
    } catch {
      // Entity load failed
    }
  };

  const handleAddNew = () => {
    setEditEntity(null);
    setIsAdding(true);
  };

  const handleSave = (data: Partial<LegalEntity>) => {
    upsert.mutate(data, {
      onSuccess: () => {
        setIsAdding(false);
        // Reload the entity after save
        if (data.legalName) {
          handleSelectEntity(data.legalName);
        }
      },
    });
  };

  const handleRename = () => {
    if (renameState && renameState.newName.trim()) {
      rename.mutate(
        { origLegalCompanyName: renameState.origName, newLegalCompanyName: renameState.newName },
        { onSuccess: () => setRenameState(null) }
      );
    }
  };

  if (isLoading) {
    return <CardLoading />;
  }

  return (
    <div>
      <div className="card">
        <div className="card-header tc-card-header">
          <h5 className="mb-0" style={{ color: "white" }}>Legal Entities</h5>
        </div>
        <div className="card-body">
          <AgentReadOnlyBanner />
          <div className="d-flex justify-content-between align-items-center mb-0">
            <h5 className="m-0">Legal Entity</h5>
            <div className="d-flex align-items-center gap-2">
              {isAdding && (
                <button className="btn btn-outline-secondary" onClick={() => {
                  setIsAdding(false);
                  if (legalEntities && legalEntities.length > 0) {
                    const defaultEntity = legalEntities.find((le) => le.isDefault) ?? legalEntities[0];
                    handleSelectEntity(defaultEntity.legalName);
                  }
                }}>
                  Cancel
                </button>
              )}
              <button
                className="btn"
                style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
                onClick={handleAddNew}
                disabled={!canWrite}
                title={!canWrite ? AGENT_READ_ONLY_MESSAGE : undefined}
              >
                <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
                  <path d="M416 277.333H277.333V416h-42.666V277.333H96v-42.666h138.667V96h42.666v138.667H416v42.666z" />
                </svg>
                <span style={{ verticalAlign: "middle" }}>Add new Legal Entity</span>
              </button>
            </div>
          </div>
          <p className="text-muted mb-3">
            Specify the Legal Entity that uses these phone numbers. This information is required for the phone numbers to be remediated.
          </p>

          {/* Rename modal */}
          {renameState && (
            <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "white" }}>
                    <h5 className="modal-title" style={{ color: "white" }}>Rename Legal Company Name</h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setRenameState(null)}
                    />
                  </div>
                  <div className="modal-body">
                    <label className="form-label">Legal Company Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={renameState.newName}
                      onChange={(e) => setRenameState({ ...renameState, newName: e.target.value })}
                    />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-outline-secondary" onClick={() => setRenameState(null)}>
                      Cancel
                    </button>
                    <button
                      className="btn"
                      style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
                      onClick={handleRename}
                      disabled={rename.isPending}
                    >
                      {rename.isPending ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Legal Entity form — always visible */}
          {isAdding ? (
            <div>
              <LegalEntityForm
                onSave={handleSave}
                isSaving={upsert.isPending}
                disabled={!canWrite}
              />
            </div>
          ) : editEntity ? (
            <LegalEntityForm
              key={editEntity.legalEntityId}
              initialData={editEntity}
              onSave={handleSave}
              isSaving={upsert.isPending}
              legalEntities={legalEntities}
              onSelectEntity={handleSelectEntity}
              disabled={!canWrite}
              // Withheld for Agents so the "Rename Legal Entity" link is not rendered.
              // It is an <a>, which ignores `disabled`, and the form already hides it
              // when no handler is supplied — cheaper than styling a dead link.
              onRenameClick={!canWrite ? undefined : (name) => setRenameState({ origName: name, newName: name })}
              onDefaultChange={(isDefault) => {
                const updated = { ...editEntity, isDefault };
                setEditEntity(updated);
                handleSave(updated);
              }}
            />
          ) : (
            <p className="text-muted">No legal entities configured.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegalEntityList;
